import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Star, Quote, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", rating: 5, message: "" });
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data);
    } catch {
      setReviews([]);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        name: form.name.trim(),
        rating: Number(form.rating),
        message: form.message.trim(),
      });
      toast.success("Thanks! Your review's in — we'll post it once we've read it.");
      setForm({ name: "", rating: 5, message: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  const showTiles = reviews.slice(0, 6);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="trio-section py-24 lg:py-32 border-b border-[var(--trio-border)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-4">
              From the guests
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9]">
              What folks<br /><span className="text-[var(--trio-primary)]">are saying.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <button
              onClick={() => setOpen(true)}
              data-testid="review-open-btn"
              className="inline-flex items-center gap-3 border-2 border-[var(--trio-text)] hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] px-6 py-3 text-xs tracking-widest uppercase font-bold transition-colors"
            >
              <Star className="w-4 h-4" /> Leave a Review
            </button>
          </div>
        </div>

        {showTiles.length === 0 ? (
          <div className="text-center py-12 text-[var(--trio-muted)] bg-[var(--trio-surface)] border border-[var(--trio-border)]">
            Be the first to leave a review.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {showTiles.map((r, i) => (
              <article
                key={r.id}
                data-testid={`review-card-${i}`}
                className={`relative p-8 border ${
                  r.featured
                    ? "bg-[var(--trio-text)] text-[var(--trio-bg)] border-[var(--trio-text)]"
                    : "bg-[var(--trio-surface)] border-[var(--trio-border)]"
                }`}
              >
                <Quote className={`w-8 h-8 mb-4 ${r.featured ? "text-[var(--trio-accent)]" : "text-[var(--trio-primary)]/40"}`} />
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${
                        s < r.rating
                          ? "fill-[var(--trio-accent)] text-[var(--trio-accent)]"
                          : r.featured
                          ? "text-[var(--trio-bg)]/20"
                          : "text-[var(--trio-border)]"
                      }`}
                    />
                  ))}
                </div>
                <p className={`text-base leading-relaxed mb-5 ${r.featured ? "" : "text-[var(--trio-muted)]"}`}>
                  “{r.message}”
                </p>
                <div className="font-display uppercase text-sm tracking-widest">
                  — {r.name}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="review-dialog"
          className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-md"
        >
          <DialogHeader>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
              Leave a Review
            </div>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              Tell us how it was.
            </DialogTitle>
            <DialogDescription className="text-sm text-[var(--trio-muted)]">
              We read every one. Approved reviews show up here on the homepage.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="review-form">
            <div>
              <Label className="text-xs tracking-widest uppercase">Your Name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                data-testid="review-name"
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Rating</Label>
              <div className="mt-2 flex gap-1" data-testid="review-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, rating: n }))}
                    data-testid={`review-star-${n}`}
                    className="p-1"
                    aria-label={`${n} star`}
                  >
                    <Star
                      className={`w-7 h-7 transition-colors ${
                        n <= form.rating
                          ? "fill-[var(--trio-accent)] text-[var(--trio-accent)]"
                          : "text-[var(--trio-border)] hover:text-[var(--trio-accent)]"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase">Your Review</Label>
              <Textarea
                required
                rows={4}
                minLength={3}
                maxLength={800}
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                data-testid="review-message"
                placeholder="What did you love? The catfish? The vibe? The staff?"
                className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="review-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? "Sending…" : "Submit Review"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
